
import React from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { getBlogsBySlug } from "../helpers/api/BlogService";
import { toast } from 'react-toastify';
import { Navbar1 } from "components/navbars";
import { Footer1 } from "components/footer";
import Seo from "components/Seo";
import FeatherIcon from 'feather-icons-react';
import '../styles/industry-cards.css';
import BackToTop from "components/BackToTop";
import { usePageTracking } from "helpers/usePageTracking";


import ScaleIcon from "../assets/images/icons/duotone-icons/general/Scale.svg";
import CalculatorIcon from "../assets/images/icons/duotone-icons/shopping/Calculator.svg";
import DollarIcon from "../assets/images/icons/duotone-icons/shopping/Dollar.svg";
import WalletIcon from "../assets/images/icons/duotone-icons/shopping/Wallet.svg";
import ShieldCheckIcon from "../assets/images/icons/duotone-icons/general/Shield-check.svg";
import Price1Icon from "../assets/images/icons/duotone-icons/shopping/Price #1.svg";
import UserIcon from "../assets/images/icons/duotone-icons/general/User.svg";
import GroupIcon from "../assets/images/icons/duotone-icons/communication/Group.svg";
import AddUserIcon from "../assets/images/icons/duotone-icons/communication/Add-user.svg";
import FileDoneIcon from "../assets/images/icons/duotone-icons/files/File-done.svg";
import { colors } from "styles/colors";
import { Col, Row } from "react-bootstrap";

const industries = [
  {
    key: "law-firms",
    title: "CRM for Law Firms",
    desc: [
      "Capture every lead & inquiry",
      "Automate follow-ups and reminders",
      "Track cases and deadlines easily"
    ],
    iconType: "svg",
    icon: ScaleIcon
  },
  {
    key: "accountants",
    title: "CRM for Accountants",
    desc: [
      "Never miss deadlines or follow-ups",
      "Automate reminders & workflows",
      "Track every client and opportunity"
    ],
    iconType: "svg",
    icon: CalculatorIcon
  },
  {
    key: "financial-advisors",
    title: "CRM for Financial Advisors",
    desc: [
      "Track every client & opportunity",
      "Automate follow-ups and reminders",
      "Segment clients for better service"
    ],
    iconType: "feather",
    icon: "dollar-sign"
  },
  {
    key: "mortgage-brokers",
    title: "CRM for Mortgage Brokers",
    desc: [
      "Capture and track every lead & deal",
      "Automate follow-ups and document reminders",
      "See pipeline status and close more loans"
    ],
    iconType: "feather",
    icon: "credit-card"
  },
  {
    key: "insurance-agents",
    title: "CRM for Insurance Agents",
    desc: [
      "Track every lead, policy & renewal",
      "Automate follow-ups and reminders",
      "Boost conversions and client retention"
    ],
    iconType: "feather",
    icon: "shield"
  },
  {
    key: "tax-consultants",
    title: "CRM for Tax Consultants",
    desc: [
      "Track clients and filing workflows",
      "Automate document and deadline reminders",
      "Boost revenue with repeat business"
    ],
    iconType: "feather",
    icon: "file-text"
  },
  {
    key: "business-consultants",
    title: "CRM for Business Consultants",
    desc: [
      "Track leads, deals, and clients easily",
      "Automate follow-ups and proposals",
      "Boost conversions and scale your business"
    ],
    iconType: "feather",
    icon: "users"
  },
  {
    key: "hr-consultants",
    title: "CRM for HR Consultants",
    desc: [
      "Track clients, jobs, and candidate pipelines",
      "Automate follow-ups and interview reminders",
      "Improve placements and client satisfaction"
    ],
    iconType: "feather",
    icon: "users"
  },
  {
    key: "recruitment-agencies",
    title: "CRM for Recruitment Agencies",
    desc: [
      "Track clients, jobs, and candidate pipelines",
      "Automate follow-ups and interview reminders",
      "Place candidates faster and scale your agency"
    ],
    iconType: "feather",
    icon: "user-plus"
  },
  {
    key: "auditors",
    title: "CRM for Auditors",
    desc: [
      "Track clients and audit engagements",
      "Automate reminders and workflow tasks",
      "Ensure deadlines and boost efficiency"
    ],
    iconType: "feather",
    icon: "check-circle"
  },
];

export default function Industries() {
  const seo = usePageTracking("industries");
  const navigate = useNavigate();

  // Helper to convert title to slug
  // const titleToSlug = (title: string) =>
  //   title
  //     .toLowerCase()
  //     .replace(/[^a-z0-9 ]/g, "")
  //     .replace(/ /g, "-");



  const titleToSlug = (title: string) =>
    title
      .toLowerCase()
      .replace(/–/g, "-")        // fix EN DASH
      .replace(/—/g, "-")        // fix EM DASH
      .replace(/[^a-z0-9\s-]/g, "") // remove special chars except -
      .replace(/\s+/g, "-")      // spaces → -
      .replace(/-+/g, "-")       // multiple - → single
      .trim();

  // Card click handler
  const handleCardClick = async (title: string) => {
    const industryKey = titleToSlug(title);
    try {
      const data = await getBlogsBySlug(industryKey);
      if (data && data.success && data.result) {
        navigate(`/${industryKey}`, {
          state: {
            type: "industry",   // ✅ IMPORTANT
            data: data.result
          }
        });
      } else {
        toast.info("No details found for this industry.");
      }
    } catch (err) {
      toast.error("Failed to fetch industry details.");
    }
  };

  return (
    <>
      {seo && (
        <Seo
          pageName="industries"
          title="CRM for Every Industry | SalesTrackify CRM Solutions"
          description="Discover industry-specific CRM solutions by SalesTrackify. Built for law firms, accountants, financial advisors, recruiters, and more."
          url="https://www.salestrackify.com/industries"
          image="/seo/trackify-preview.png"
          keywords={industries.map(ind => ind.title).join(', ')}
        />
      )}
      <Navbar1
        hideSearch
        fixedWidth
        navClass="navbar-light zindex-10"
        buttonClass="btn-outline-secondary btn-sm"
      />
      <div className="">
        <section
          style={{
            background: 'linear-gradient(90deg, #1e3a8a22, #2dd4bf22, #1e3a8a22)',
            padding: '80px 0',
            textAlign: 'center',
            borderBottomLeftRadius: '30px',
            borderBottomRightRadius: '30px',
            marginLeft: 25,
            marginRight: 25,
            marginBottom: '2.5rem',
          }}
        >

          <div
            className="d-inline-flex align-items-center mb-4"
            style={{
              gap: '8px',
              background: 'rgba(36,52,125,0.08)',
              borderRadius: '50px',
              padding: '8px 20px',
              border: '1px solid rgba(36,52,125,0.1)',
            }}
          >
            <FeatherIcon icon="zap" style={{ width: '14px', height: '14px', color: colors.primary.darkBlue }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: colors.primary.darkBlue }}>
              Industries
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: 700,
              color: '#24347D',
            }}
          >
            CRM for Every Industry | SalesTrackify CRM Solutions
          </h1>
          <p style={{ marginTop: '10px', fontSize: '18px', color: '#6c757d', textAlign: 'center', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }} className="px-5">
            Discover industry-specific CRM solutions by SalesTrackify. Built for law firms, accountants, financial advisors, recruiters, and more.
          </p>
        </section>
        <section className="container industry-sub-header text-center">
          <h2>Industry-Specific CRM Built for Your Business</h2>
          <p>
            SalesTrackify is designed to adapt to your industry workflows — from managing clients and pipelines to automating support and reporting.
          </p>
          {/* <Link to="/signup" className="btn btn-primary mx-2">Get Started Free</Link> */}
          {/* <a href="/contact" className="btn btn-outline-primary mx-2">Book a Demo</a> */}
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold transition-all hover:scale-105 shadow-lg"
            style={{
              backgroundColor: "#26A2AB",
              color: "white"
            }}
          >
            Book a Demo
          </Link>
        </section>

        <section className="container mb-5">
          <div className="row justify-content-center text-center mb-4">
            <div className="col-lg-9">
              <span
                className="fw-semibold text-primary px-3 py-1 rounded-pill"
                style={{
                  backgroundColor: '#eef2ff',
                  fontSize: '14px',
                }}
              >
                Quick Links
              </span>
              <h2 className="fw-bold mt-3" style={{ fontSize: '2.3rem', color: '#24347D' }}>
                Jump to the most important pages
              </h2>
              <p className="text-muted mx-auto" style={{ maxWidth: '680px', marginTop: '0.8rem' }}>
                Navigate quickly to core content that supports your industry research and CRM decisions.
              </p>
            </div>
          </div>
        </section>


        <section>
          {/* Navigation Cards */}
          <Row className="g-4 justify-content-center">
            {[
              {
                title: "About SalesTrackify",
                desc: "Get to know our story, mission, and the team behind this powerful CRM platform.",
                icon: "info",
                to: "/about",
                accent: "#3b82f6",
                bgAccent: "#eef6ff",
              },
              {
                title: "All Features",
                desc: "Discover all the powerful features that make SalesTrackify the CRM of choice.",
                icon: "grid",
                to: "/features",
                accent: "#8b5cf6",
                bgAccent: "#f5f3ff",
              },
              {
                title: "Blogs",
                desc: "Read insights, tips, and best practices for sales success and CRM management.",
                icon: "book-open",
                to: "/blogs",
                accent: "#06b6d4",
                bgAccent: "#ecf8ff",
              },
            ].map((item, index) => (
              <Col lg={3} md={6} sm={12} key={index}>
                <Link
                  to={item.to}
                  className="text-decoration-none"
                  style={{ textDecoration: "none" }}
                >
                  <div
                    className="h-100 bg-white rounded-4 p-5 text-center shadow-sm transition"
                    style={{
                      border: "2px solid #e5e7eb",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = item.accent;
                      e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.12)";
                      e.currentTarget.style.transform = "translateY(-12px)";

                      const icon = e.currentTarget.querySelector(".interlink-icon") as HTMLElement | null;
                      if (icon) {
                        icon.style.background = item.bgAccent;
                        const iconElement = icon.querySelector("svg") as HTMLElement | null;
                        if (iconElement) iconElement.style.color = item.accent;
                      }

                      const title = e.currentTarget.querySelector(".interlink-title") as HTMLElement | null;
                      if (title) title.style.color = item.accent;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                      e.currentTarget.style.transform = "translateY(0)";

                      const icon = e.currentTarget.querySelector(".interlink-icon") as HTMLElement | null;
                      if (icon) {
                        icon.style.background = item.bgAccent;
                        const iconElement = icon.querySelector("svg") as HTMLElement | null;
                        if (iconElement) iconElement.style.color = item.accent;
                      }

                      const title = e.currentTarget.querySelector(".interlink-title") as HTMLElement | null;
                      if (title) title.style.color = colors.primary.darkBlue;
                    }}
                  >
                    {/* Icon */}
                    <div
                      className="interlink-icon d-flex align-items-center justify-content-center mx-auto mb-4"
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "14px",
                        background: item.bgAccent,
                        transition: "all 0.3s ease",
                      }}
                    >
                      <FeatherIcon
                        icon={item.icon}
                        size={28}
                        style={{
                          color: item.accent,
                          transition: "color 0.3s ease",
                        }}
                      />
                    </div>

                    {/* Title */}
                    <h5
                      className="interlink-title fw-bold mb-3"
                      style={{
                        color: colors.primary.darkBlue,
                        fontSize: "18px",
                        transition: "color 0.3s ease",
                      }}
                    >
                      {item.title}
                    </h5>

                    {/* Description */}
                    <p
                      className="text-muted small mb-3"
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.6",
                      }}
                    >
                      {item.desc}
                    </p>

                    {/* Arrow Button */}
                    <div
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        gap: "8px",
                        color: item.accent,
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      Explore
                      <FeatherIcon icon="arrow-right" size={16} />
                    </div>
                  </div>
                </Link>
              </Col>
            ))}
          </Row>
        </section>










        <section className="container my-5">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div style={{
                background: 'linear-gradient(90deg, #f8fafc 80%, #e0f2fe 100%)',
                borderRadius: 24,
                boxShadow: '0 2px 16px 0 rgba(36,52,125,0.08)',
                padding: '36px 32px',
                marginBottom: 24,
                border: '1.5px solid #e5e7eb',
                position: 'relative',
              }}>
                <div className="d-flex align-items-start mb-3">
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #2cd8d5 60%, #6b8dd6 100%)',
                    borderRadius: '50%',
                    width: 54,
                    height: 54,
                    marginRight: 18,
                  }}>
                    <FeatherIcon icon="layers" size={28} color="#fff" />
                  </span>
                  <div>
                    <h3 style={{ fontWeight: 700, color: '#24347D', fontSize: 22, marginBottom: 8 }}>Industry-Specific CRM Challenges</h3>
                    <p style={{ fontSize: 17, color: '#374151', marginBottom: 0 }}>
                      Every industry has unique processes, compliance requirements, and customer expectations. A generic CRM often fails to address these nuances.
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-start">
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #6b8dd6 60%, #2cd8d5 100%)',
                    borderRadius: '50%',
                    width: 54,
                    height: 54,
                    marginRight: 18,
                  }}>
                    <FeatherIcon icon="cpu" size={28} color="#fff" />
                  </span>
                  <div>
                    <h3 style={{ fontWeight: 700, color: '#24347D', fontSize: 22, marginBottom: 8 }}>How SalesTrackify Helps</h3>
                    <p style={{ fontSize: 17, color: '#374151', marginBottom: 0 }}>
                      SalesTrackify provides customizable, AI-powered CRM solutions tailored for different industries — helping you manage leads, clients, support tickets, and revenue pipelines efficiently.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="container industry-sub-header">
          <h2 className="mb-4 text-center">Industries We Serve</h2>
          <div className="row g-4 justify-content-center">
            {industries.map((ind, idx) => (
              <div className="col-md-6 col-lg-5 d-flex justify-content-center mb-4" key={ind.key}>
                <div
                  role="button"
                  tabIndex={0}
                  className="industry-card-horizontal"
                  onClick={() => handleCardClick(ind.title)}
                  onKeyPress={e => { if (e.key === 'Enter') handleCardClick(ind.title); }}
                >
                  <div className="industry-card-accent" />
                  <div className="industry-card-icon-wrap">
                    <div className="industry-card-icon-inner">
                      {ind.iconType === 'svg' ? (
                        <img src={ind.icon} alt={ind.title + ' icon'} width={32} height={32} />
                      ) : (
                        <FeatherIcon icon={ind.icon} size={32} color="#fff" />
                      )}
                    </div>
                  </div>
                  <div className="industry-card-content">
                    <h3 className="fw-bold mb-2 text-dark industry-card-title">{ind.title}</h3>
                    <ul className="industry-card-list">
                      {ind.desc.map((point: string, i: number) => (
                        <li key={i}>{point}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section
          className="container"
          style={{ marginTop: '56px', marginBottom: '56px' }}
        >
          <div
            style={{
              borderRadius: '24px',
              border: '1px solid #e8ebf2',
              padding: 'clamp(1.25rem, 3vw, 2.25rem)',
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
              boxShadow: '0 12px 34px rgba(36,52,125,0.08)',
            }}
          >
            <div className="text-center mb-4">
              <div
                className="d-inline-flex align-items-center"
                style={{
                  gap: '8px',
                  background: '#eef2ff',
                  borderRadius: '50px',
                  padding: '6px 16px',
                  marginBottom: '12px',
                }}
              >
                <FeatherIcon icon="briefcase" style={{ width: '14px', height: '14px', color: colors.primary.darkBlue }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: colors.primary.darkBlue }}>
                  Industry-fit CRM
                </span>
              </div>

              <h2 style={{ fontSize: 'clamp(1.35rem, 3vw, 2rem)', fontWeight: 800, color: '#1a1a2e', marginBottom: '10px' }}>
                Why Choose CRM for Your Industry?
              </h2>
            </div>

            <div className="row g-3 g-md-4">
              <div className="col-12 col-md-6 col-lg-4">
                <div
                  className="h-100"
                  style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '16px',
                    padding: '18px',
                  }}
                >
                  <div className="d-flex align-items-center mb-2" style={{ gap: '10px' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FeatherIcon icon="layers" style={{ width: '15px', height: '15px', color: colors.primary.darkBlue }} />
                    </div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1f2937' }}>Industry Challenges</h3>
                  </div>
                  <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.75, fontSize: '0.99rem' }}>
                    Every industry has unique challenges when it comes to managing customers, tracking leads, and improving sales performance. A generic CRM system may not be enough to handle specific workflows and business processes.
                  </p>
                </div>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <div
                  className="h-100"
                  style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '16px',
                    padding: '18px',
                  }}
                >
                  <div className="d-flex align-items-center mb-2" style={{ gap: '10px' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#e8f7f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FeatherIcon icon="cpu" style={{ width: '15px', height: '15px', color: colors.primary.teal }} />
                    </div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1f2937' }}>How SalesTrackify Helps</h3>
                  </div>
                  <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.75, fontSize: '0.99rem' }}>
                    SalesTrackify provides industry-specific CRM solutions that help businesses automate repetitive tasks, manage customer relationships, and gain valuable insights through analytics. With lead tracking, pipeline management, and automated communication, your team can focus more on closing deals and less on manual work.
                  </p>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div
                  className="h-100"
                  style={{
                    background: 'linear-gradient(155deg, #24347D 0%, #26A2AB 100%)',
                    border: '1px solid rgba(36,52,125,0.2)',
                    borderRadius: '16px',
                    padding: '18px',
                    color: '#fff',
                  }}
                >
                  <div className="d-flex align-items-center mb-2" style={{ gap: '10px' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FeatherIcon icon="trending-up" style={{ width: '15px', height: '15px', color: '#fff' }} />
                    </div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#fff' }}>Business Growth Outcomes</h3>
                  </div>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.86)', lineHeight: 1.75, fontSize: '0.99rem' }}>
                    Whether you are in finance, consulting, legal services, or recruitment, our CRM platform is designed to adapt to your needs. With the right CRM software, businesses improve efficiency, enhance customer satisfaction, and achieve sustainable growth.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* <section className="container mt-5 text-center mb-5">
          <h2>Start managing your business smarter with SalesTrackify CRM.</h2>

          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold transition-all hover:scale-105 shadow-lg"
            style={{
              backgroundColor: "#26A2AB",
              color: "white"
            }}
          >
            Book a Demo
          </Link>
        </section> */}
      </div>
      <Footer1 />
      {/* <BackToTop /> */}
    </>
  );
}
